/**
 * Roles & Permissions — administrator-only page.
 *
 * Two-column layout:
 *   left  → list of roles (RoleSelector)
 *   right → permission matrix (PermissionMatrix) for the selected role +
 *           a "Reset to default" affordance.
 *
 * Mock-mode only: toggling permissions updates a local copy of the role's
 * permission set with a toast. Nothing hits the backend. The "Create custom
 * role" sheet captures a name + description but is also pure mock.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, RotateCcw } from "lucide-react";

import {
  Badge,
  Button,
  Input,
  Label,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  cn,
  toast,
} from "@bizop/ui-kit";
import {
  DEFAULT_ROLE_PERMISSIONS,
  type PermissionKey,
  type RoleKey,
} from "@bizop/shared/rbac";

import { PageHeader } from "../../../components/PageHeader";
import { PermissionMatrix } from "../../../components/people/PermissionMatrix";
import { RoleSelector } from "../../../components/people/RoleSelector";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  PERMISSION_GROUPS,
  mockRoles,
  type MockRole,
} from "../../../lib/rolesMock";
import { ROLE_LABELS, useRoleStore } from "../../../state/roleStore";

export const Route = createFileRoute("/_app/people/roles")({
  component: RolesPage,
});

function RolesPage() {
  const [roles, setRoles] = usePersistentMockState<MockRole[]>(
    "roles",
    mockRoles.filter((r) => r.key !== "dealer"),
  );
  const [selectedKey, setSelectedKey] = useState<RoleKey>(roles[0]?.key ?? "admin");
  const [createOpen, setCreateOpen] = useState(false);

  const selectedRole = useMemo(
    () => roles.find((r) => r.key === selectedKey),
    [roles, selectedKey],
  );

  const totalPermissions = useMemo(
    () => PERMISSION_GROUPS.reduce((sum, g) => sum + g.permissions.length, 0),
    [],
  );

  function setRolePermissions(
    roleKey: RoleKey,
    next: PermissionKey[] | ((prev: PermissionKey[]) => PermissionKey[]),
  ) {
    setRoles((prev) =>
      prev.map((r) =>
        r.key === roleKey
          ? {
              ...r,
              permissions:
                typeof next === "function"
                  ? next(r.permissions)
                  : [...next],
            }
          : r,
      ),
    );
  }

  function handleToggle(roleKey: RoleKey, key: PermissionKey, enabled: boolean) {
    setRolePermissions(roleKey, (prev) => {
      const permSet = new Set(prev);
      if (enabled) permSet.add(key);
      else permSet.delete(key);
      const nextPerms = Array.from(permSet);
      // Persist the override to the role store so the role switcher picks it up
      useRoleStore.getState().setCustomPermissions(roleKey, nextPerms);
      return nextPerms;
    });
    toast.info(
      enabled
        ? `Granted ${key} to ${ROLE_LABELS[roleKey]}`
        : `Revoked ${key} from ${ROLE_LABELS[roleKey]}`,
    );
  }

  function handleResetToDefault(roleKey: RoleKey) {
    if (DEFAULT_ROLE_PERMISSIONS[roleKey]) {
      setRolePermissions(roleKey, [...DEFAULT_ROLE_PERMISSIONS[roleKey]]);
      useRoleStore.getState().clearCustomPermissions(roleKey);
      toast.success(
        `Reset ${ROLE_LABELS[roleKey]} to default permissions (mock)`,
      );
    }
  }

  function handleCreateCustom(values: { name: string; description: string }) {
    const id = `role_custom_${Date.now().toString(36)}`;
    const customKey = id as RoleKey;
    const newRole: MockRole = {
      _id: id,
      key: customKey,
      displayName: values.name,
      description: values.description,
      permissions: [],
      isSystem: false,
    };
    setRoles((prev) => [...prev, newRole]);
    setSelectedKey(customKey);
    setCreateOpen(false);
    toast.success(`Created custom role "${values.name}"`, {
      description: "Toggle permissions on the right to grant access.",
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Roles & Permissions"
        description="Map workflow areas to roles. Permission grants flow into InstantDB authorisation in production."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Create custom role
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-5">
        <aside>
          <div className="mb-2 flex items-center justify-between px-1">
            <h2 className="text-[12px] font-medium uppercase tracking-wide text-(--text-tertiary)">
              Roles
            </h2>
            <Badge variant="neutral">{roles.length}</Badge>
          </div>
          <RoleSelector
            roles={roles}
            selectedKey={selectedKey}
            onSelect={(key) => setSelectedKey(key as RoleKey)}
          />
        </aside>

        <section className="min-w-0">
          {selectedRole ? (
            <>
              <div
                className={cn(
                  "mb-4 rounded-md border border-(--border-subtle) bg-(--bg-surface) p-4",
                  "flex flex-wrap items-start justify-between gap-3",
                )}
              >
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-[15px] font-semibold text-(--text-primary)">
                      {ROLE_LABELS[selectedRole.key as RoleKey] ??
                        selectedRole.displayName}
                    </h3>
                    {selectedRole.isSystem ? (
                      <Badge variant="neutral">System</Badge>
                    ) : (
                      <Badge variant="success">Custom</Badge>
                    )}
                    <span className="font-mono text-[11px] text-(--text-tertiary) tabular-nums">
                      {selectedRole.permissions.length}/{totalPermissions}{" "}
                      permissions
                    </span>
                  </div>
                  <p className="mt-1 text-[12px] text-(--text-secondary)">
                    {selectedRole.description}
                  </p>
                </div>
                {selectedRole.isSystem ? (
                  <Button
                    variant="outline"
                    onClick={() =>
                      handleResetToDefault(selectedRole.key as RoleKey)
                    }
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Reset to default
                  </Button>
                ) : null}
              </div>

              <PermissionMatrix
                enabled={(k) => selectedRole.permissions.includes(k)}
                onToggle={(k, enabled) =>
                  handleToggle(selectedRole.key as RoleKey, k, enabled)
                }
              />
            </>
          ) : (
            <div className="rounded-md border border-dashed border-(--border-subtle) bg-(--bg-canvas) p-6 text-center text-[13px] text-(--text-tertiary)">
              Pick a role from the list to view its permissions.
            </div>
          )}
        </section>
      </div>

      {/* Create custom role */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>Create custom role</SheetTitle>
            <SheetDescription>
              Start with no permissions and grant them in the matrix once
              the role is created.
            </SheetDescription>
          </SheetHeader>
          <CreateRoleForm
            onSubmit={handleCreateCustom}
            onCancel={() => setCreateOpen(false)}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}

function CreateRoleForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (values: { name: string; description: string }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError("Role name is required");
      return;
    }
    onSubmit({ name: name.trim(), description: description.trim() });
  }

  return (
    <form onSubmit={handleSubmit} className="contents">
      <SheetBody>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="role-name">Role name</Label>
            <Input
              id="role-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Regional manager"
            />
            {error ? (
              <p className="text-[11px] text-(--danger)">{error}</p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="role-desc">Description</Label>
            <Input
              id="role-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this role do?"
            />
          </div>
        </div>
      </SheetBody>
      <SheetFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">Create role</Button>
      </SheetFooter>
    </form>
  );
}
